// Mock data representing what an OEM site crawl would return

export type MatchConfidence = "high" | "medium" | "low" | "not_found";

export interface DiscoveredVariant {
  screenSize: number;
  variantLabel: string;
  modelNumber: string;
  msrp: number;
  url: string;
}

export interface DiscoveredProductLine {
  id: string;
  name: string;
  displayTechnology: string;
  resolution: string;
  refreshRate: number;
  smartPlatform: string;
  year: number;
  tier: "flagship" | "premium" | "mid-range" | "value";
  variants: DiscoveredVariant[];
  selected: boolean;
}

export interface DiscoveredBrandCatalog {
  brandId: string;
  brandName: string;
  brandDomain: string;
  crawledAt: string;
  crawledUrl: string;
  productLines: DiscoveredProductLine[];
}

export interface RetailerMatch {
  retailerId: string;
  retailerName: string;
  retailerDomain: string;
  url: string;
  retailerSku: string;
  confidence: MatchConfidence;
  matchReason: string;
  priceFound: number | null;
}

export interface VariantRetailerMatches {
  modelNumber: string;
  screenSize: number;
  variantLabel: string;
  msrp: number;
  matches: RetailerMatch[];
}

export interface ProductLineRetailerMatches {
  productLineId: string;
  productLineName: string;
  variants: VariantRetailerMatches[];
}

// --- Samsung OEM Crawl Result ---

export const SAMSUNG_CATALOG: DiscoveredBrandCatalog = {
  brandId: "b1",
  brandName: "Samsung",
  brandDomain: "samsung.com",
  crawledAt: new Date().toISOString(),
  crawledUrl: "https://www.samsung.com/us/televisions-home-theater/tvs/all-tvs/",
  productLines: [
    {
      id: "d-pl1",
      name: "Samsung S95F",
      displayTechnology: "QD-OLED",
      resolution: "4K",
      refreshRate: 144,
      smartPlatform: "Tizen",
      year: 2026,
      tier: "flagship",
      selected: true,
      variants: [
        { screenSize: 55, variantLabel: '55"', modelNumber: "QN55S95FAFXZA", msrp: 1999.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/oled/55-class-s95f-oled-4k-qn55s95fafxza/" },
        { screenSize: 65, variantLabel: '65"', modelNumber: "QN65S95FAFXZA", msrp: 2799.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/oled/65-class-s95f-oled-4k-qn65s95fafxza/" },
        { screenSize: 77, variantLabel: '77"', modelNumber: "QN77S95FAFXZA", msrp: 3799.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/oled/77-class-s95f-oled-4k-qn77s95fafxza/" },
      ],
    },
    {
      id: "d-pl2",
      name: "Samsung S90F",
      displayTechnology: "OLED",
      resolution: "4K",
      refreshRate: 144,
      smartPlatform: "Tizen",
      year: 2026,
      tier: "premium",
      selected: true,
      variants: [
        { screenSize: 55, variantLabel: '55"', modelNumber: "QN55S90FAFXZA", msrp: 1399.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/oled/55-class-s90f-oled-4k-qn55s90fafxza/" },
        { screenSize: 65, variantLabel: '65"', modelNumber: "QN65S90FAFXZA", msrp: 1699.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/oled/65-class-s90f-oled-4k-qn65s90fafxza/" },
        { screenSize: 75, variantLabel: '75"', modelNumber: "QN75S90FAFXZA", msrp: 2299.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/oled/75-class-s90f-oled-4k-qn75s90fafxza/" },
        { screenSize: 83, variantLabel: '83"', modelNumber: "QN83S90FAFXZA", msrp: 2999.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/oled/83-class-s90f-oled-4k-qn83s90fafxza/" },
      ],
    },
    {
      id: "d-pl3",
      name: "Samsung S85F",
      displayTechnology: "OLED",
      resolution: "4K",
      refreshRate: 120,
      smartPlatform: "Tizen",
      year: 2026,
      tier: "premium",
      selected: true,
      variants: [
        { screenSize: 55, variantLabel: '55"', modelNumber: "QN55S85FAFXZA", msrp: 1099.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/oled/55-class-s85f-oled-4k-qn55s85fafxza/" },
        { screenSize: 65, variantLabel: '65"', modelNumber: "QN65S85FAFXZA", msrp: 1399.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/oled/65-class-s85f-oled-4k-qn65s85fafxza/" },
        { screenSize: 77, variantLabel: '77"', modelNumber: "QN77S85FAFXZA", msrp: 1999.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/oled/77-class-s85f-oled-4k-qn77s85fafxza/" },
      ],
    },
    {
      id: "d-pl4",
      name: "Samsung QN90F",
      displayTechnology: "Neo QLED",
      resolution: "4K",
      refreshRate: 144,
      smartPlatform: "Tizen",
      year: 2026,
      tier: "premium",
      selected: true,
      variants: [
        { screenSize: 55, variantLabel: '55"', modelNumber: "QN55QN90FAFXZA", msrp: 1299.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/neo-qled/55-class-qn90f-neo-qled-4k-qn55qn90fafxza/" },
        { screenSize: 65, variantLabel: '65"', modelNumber: "QN65QN90FAFXZA", msrp: 1699.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/neo-qled/65-class-qn90f-neo-qled-4k-qn65qn90fafxza/" },
        { screenSize: 75, variantLabel: '75"', modelNumber: "QN75QN90FAFXZA", msrp: 2299.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/neo-qled/75-class-qn90f-neo-qled-4k-qn75qn90fafxza/" },
        { screenSize: 85, variantLabel: '85"', modelNumber: "QN85QN90FAFXZA", msrp: 2999.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/neo-qled/85-class-qn90f-neo-qled-4k-qn85qn90fafxza/" },
      ],
    },
    {
      id: "d-pl5",
      name: "Samsung QN900F",
      displayTechnology: "Neo QLED",
      resolution: "8K",
      refreshRate: 144,
      smartPlatform: "Tizen",
      year: 2026,
      tier: "flagship",
      selected: false,
      variants: [
        { screenSize: 65, variantLabel: '65"', modelNumber: "QN65QN900FAFXZA", msrp: 3499.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/neo-qled/65-class-qn900f-neo-qled-8k-qn65qn900fafxza/" },
        { screenSize: 75, variantLabel: '75"', modelNumber: "QN75QN900FAFXZA", msrp: 4499.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/neo-qled/75-class-qn900f-neo-qled-8k-qn75qn900fafxza/" },
        { screenSize: 85, variantLabel: '85"', modelNumber: "QN85QN900FAFXZA", msrp: 5999.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/neo-qled/85-class-qn900f-neo-qled-8k-qn85qn900fafxza/" },
      ],
    },
    {
      id: "d-pl6",
      name: "Samsung Q70F",
      displayTechnology: "QLED",
      resolution: "4K",
      refreshRate: 120,
      smartPlatform: "Tizen",
      year: 2026,
      tier: "mid-range",
      selected: false,
      variants: [
        { screenSize: 55, variantLabel: '55"', modelNumber: "QN55Q70FAFXZA", msrp: 649.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/qled/55-class-q70f-qled-4k-qn55q70fafxza/" },
        { screenSize: 65, variantLabel: '65"', modelNumber: "QN65Q70FAFXZA", msrp: 799.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/qled/65-class-q70f-qled-4k-qn65q70fafxza/" },
        { screenSize: 75, variantLabel: '75"', modelNumber: "QN75Q70FAFXZA", msrp: 1099.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/qled/75-class-q70f-qled-4k-qn75q70fafxza/" },
        { screenSize: 85, variantLabel: '85"', modelNumber: "QN85Q70FAFXZA", msrp: 1299.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/qled/85-class-q70f-qled-4k-qn85q70fafxza/" },
      ],
    },
    {
      id: "d-pl7",
      name: 'Samsung The Frame (2026)',
      displayTechnology: "QLED",
      resolution: "4K",
      refreshRate: 60,
      smartPlatform: "Tizen",
      year: 2026,
      tier: "mid-range",
      selected: false,
      variants: [
        { screenSize: 32, variantLabel: '32"', modelNumber: "QN32LS03FBFXZA", msrp: 599.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/the-frame/32-class-the-frame-qled-4k-qn32ls03fbfxza/" },
        { screenSize: 43, variantLabel: '43"', modelNumber: "QN43LS03FBFXZA", msrp: 799.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/the-frame/43-class-the-frame-qled-4k-qn43ls03fbfxza/" },
        { screenSize: 50, variantLabel: '50"', modelNumber: "QN50LS03FBFXZA", msrp: 999.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/the-frame/50-class-the-frame-qled-4k-qn50ls03fbfxza/" },
        { screenSize: 55, variantLabel: '55"', modelNumber: "QN55LS03FBFXZA", msrp: 1199.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/the-frame/55-class-the-frame-qled-4k-qn55ls03fbfxza/" },
        { screenSize: 65, variantLabel: '65"', modelNumber: "QN65LS03FBFXZA", msrp: 1499.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/the-frame/65-class-the-frame-qled-4k-qn65ls03fbfxza/" },
        { screenSize: 75, variantLabel: '75"', modelNumber: "QN75LS03FBFXZA", msrp: 1999.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/the-frame/75-class-the-frame-qled-4k-qn75ls03fbfxza/" },
        { screenSize: 85, variantLabel: '85"', modelNumber: "QN85LS03FBFXZA", msrp: 2999.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/the-frame/85-class-the-frame-qled-4k-qn85ls03fbfxza/" },
      ],
    },
    {
      id: "d-pl8",
      name: "Samsung CU7000",
      displayTechnology: "Crystal UHD",
      resolution: "4K",
      refreshRate: 60,
      smartPlatform: "Tizen",
      year: 2026,
      tier: "value",
      selected: false,
      variants: [
        { screenSize: 43, variantLabel: '43"', modelNumber: "UN43CU7000FXZA", msrp: 249.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/crystal-uhd/43-class-cu7000-crystal-uhd-4k-un43cu7000fxza/" },
        { screenSize: 50, variantLabel: '50"', modelNumber: "UN50CU7000FXZA", msrp: 299.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/crystal-uhd/50-class-cu7000-crystal-uhd-4k-un50cu7000fxza/" },
        { screenSize: 55, variantLabel: '55"', modelNumber: "UN55CU7000FXZA", msrp: 349.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/crystal-uhd/55-class-cu7000-crystal-uhd-4k-un55cu7000fxza/" },
        { screenSize: 65, variantLabel: '65"', modelNumber: "UN65CU7000FXZA", msrp: 399.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/crystal-uhd/65-class-cu7000-crystal-uhd-4k-un65cu7000fxza/" },
        { screenSize: 70, variantLabel: '70"', modelNumber: "UN70CU7000FXZA", msrp: 499.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/crystal-uhd/70-class-cu7000-crystal-uhd-4k-un70cu7000fxza/" },
        { screenSize: 75, variantLabel: '75"', modelNumber: "UN75CU7000FXZA", msrp: 599.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/crystal-uhd/75-class-cu7000-crystal-uhd-4k-un75cu7000fxza/" },
        { screenSize: 85, variantLabel: '85"', modelNumber: "UN85CU7000FXZA", msrp: 799.99, url: "https://www.samsung.com/us/televisions-home-theater/tvs/crystal-uhd/85-class-cu7000-crystal-uhd-4k-un85cu7000fxza/" },
      ],
    },
  ],
};

// --- Retailer Match Results for Samsung S95F + S90F + S85F + QN90F ---

const RETAILER_DEFS = [
  { id: "r2", name: "Amazon", domain: "amazon.com" },
  { id: "r1", name: "Best Buy", domain: "bestbuy.com" },
  { id: "r3", name: "Walmart", domain: "walmart.com" },
  { id: "r4", name: "Target", domain: "target.com" },
  { id: "r5", name: "Costco", domain: "costco.com" },
];

function m(
  retailerIdx: number,
  confidence: MatchConfidence,
  opts: { url?: string; sku?: string; price?: number | null; reason?: string } = {},
): RetailerMatch {
  const r = RETAILER_DEFS[retailerIdx];
  return {
    retailerId: r.id,
    retailerName: r.name,
    retailerDomain: r.domain,
    url: opts.url ?? "",
    retailerSku: opts.sku ?? "",
    confidence,
    matchReason: opts.reason ?? (confidence === "high" ? "Exact model number match" : confidence === "medium" ? "Partial model match — verify" : confidence === "not_found" ? "No listing found" : "Low-confidence match"),
    priceFound: opts.price ?? null,
  };
}

export const SAMSUNG_RETAILER_MATCHES: ProductLineRetailerMatches[] = [
  // S95F
  {
    productLineId: "d-pl1",
    productLineName: "Samsung S95F",
    variants: [
      {
        modelNumber: "QN55S95FAFXZA", screenSize: 55, variantLabel: '55"', msrp: 1999.99,
        matches: [
          m(0, "high", { url: "https://www.amazon.com/dp/B0DV2GXKM1", sku: "B0DV2GXKM1", price: 1997.99 }),
          m(1, "high", { url: "https://www.bestbuy.com/site/6614042.p", sku: "6614042", price: 1999.99 }),
          m(2, "high", { url: "https://www.walmart.com/ip/5765432109", price: 1999.99 }),
          m(3, "high", { url: "https://www.target.com/p/-/A-91234567", price: 1999.99 }),
          m(4, "high", { url: "https://www.costco.com/samsung-55-s95f.product.4000234567.html", price: 1949.99, reason: "Exact match — Costco exclusive bundle SKU" }),
        ],
      },
      {
        modelNumber: "QN65S95FAFXZA", screenSize: 65, variantLabel: '65"', msrp: 2799.99,
        matches: [
          m(0, "high", { url: "https://www.amazon.com/dp/B0DV2HKM65", sku: "B0DV2HKM65", price: 2797.99 }),
          m(1, "high", { url: "https://www.bestbuy.com/site/6614043.p", sku: "6614043", price: 2799.99 }),
          m(2, "high", { url: "https://www.walmart.com/ip/5765432110", price: 2799.99 }),
          m(3, "high", { url: "https://www.target.com/p/-/A-91234568", price: 2799.99 }),
          m(4, "high", { url: "https://www.costco.com/samsung-65-s95f.product.4000234568.html", price: 2699.99 }),
        ],
      },
      {
        modelNumber: "QN77S95FAFXZA", screenSize: 77, variantLabel: '77"', msrp: 3799.99,
        matches: [
          m(0, "high", { url: "https://www.amazon.com/dp/B0DV2JKM77", sku: "B0DV2JKM77", price: 3797.99 }),
          m(1, "high", { url: "https://www.bestbuy.com/site/6614044.p", sku: "6614044", price: 3799.99 }),
          m(2, "high", { url: "https://www.walmart.com/ip/5765432111", price: 3799.99 }),
          m(3, "not_found", { reason: "Target does not carry 77\" S95F" }),
          m(4, "high", { url: "https://www.costco.com/samsung-77-s95f.product.4000234569.html", price: 3649.99 }),
        ],
      },
    ],
  },
  // S90F
  {
    productLineId: "d-pl2",
    productLineName: "Samsung S90F",
    variants: [
      {
        modelNumber: "QN55S90FAFXZA", screenSize: 55, variantLabel: '55"', msrp: 1399.99,
        matches: [
          m(0, "high", { url: "https://www.amazon.com/dp/B0DV3GXK55", sku: "B0DV3GXK55", price: 1397.99 }),
          m(1, "high", { url: "https://www.bestbuy.com/site/6614050.p", sku: "6614050", price: 1399.99 }),
          m(2, "high", { url: "https://www.walmart.com/ip/5765432115", price: 1399.99 }),
          m(3, "high", { url: "https://www.target.com/p/-/A-91234572", price: 1399.99 }),
          m(4, "not_found", { reason: "Costco does not carry 55\" S90F" }),
        ],
      },
      {
        modelNumber: "QN65S90FAFXZA", screenSize: 65, variantLabel: '65"', msrp: 1699.99,
        matches: [
          m(0, "high", { url: "https://www.amazon.com/dp/B0DV3GXK65", sku: "B0DV3GXK65", price: 1697.99 }),
          m(1, "high", { url: "https://www.bestbuy.com/site/6614051.p", sku: "6614051", price: 1699.99 }),
          m(2, "high", { url: "https://www.walmart.com/ip/5765432116", price: 1699.99 }),
          m(3, "high", { url: "https://www.target.com/p/-/A-91234573", price: 1699.99 }),
          m(4, "high", { url: "https://www.costco.com/samsung-65-s90f.product.4000234570.html", price: 1599.99 }),
        ],
      },
      {
        modelNumber: "QN75S90FAFXZA", screenSize: 75, variantLabel: '75"', msrp: 2299.99,
        matches: [
          m(0, "high", { url: "https://www.amazon.com/dp/B0DV3GXK75", sku: "B0DV3GXK75", price: 2297.99 }),
          m(1, "high", { url: "https://www.bestbuy.com/site/6614052.p", sku: "6614052", price: 2299.99 }),
          m(2, "high", { url: "https://www.walmart.com/ip/5765432117", price: 2299.99 }),
          m(3, "not_found", { reason: "Target does not carry 75\" S90F" }),
          m(4, "high", { url: "https://www.costco.com/samsung-75-s90f.product.4000234571.html", price: 2149.99 }),
        ],
      },
      {
        modelNumber: "QN83S90FAFXZA", screenSize: 83, variantLabel: '83"', msrp: 2999.99,
        matches: [
          m(0, "high", { url: "https://www.amazon.com/dp/B0DV3GXK83", sku: "B0DV3GXK83", price: 2997.99 }),
          m(1, "high", { url: "https://www.bestbuy.com/site/6614053.p", sku: "6614053", price: 2999.99 }),
          m(2, "medium", { url: "https://www.walmart.com/ip/5765432118", price: 2999.99, reason: "Model matched but listed by 3rd-party seller, not Walmart" }),
          m(3, "not_found", { reason: "Target does not carry 83\" S90F" }),
          m(4, "medium", { url: "https://www.costco.com/samsung-83-s90f.product.4000234572.html", price: 2849.99, reason: "Costco-exclusive model QN83S90FCFXZA — verify variant match" }),
        ],
      },
    ],
  },
  // S85F
  {
    productLineId: "d-pl3",
    productLineName: "Samsung S85F",
    variants: [
      {
        modelNumber: "QN55S85FAFXZA", screenSize: 55, variantLabel: '55"', msrp: 1099.99,
        matches: [
          m(0, "high", { url: "https://www.amazon.com/dp/B0DV4GXK55", sku: "B0DV4GXK55", price: 1097.99 }),
          m(1, "high", { url: "https://www.bestbuy.com/site/6614060.p", sku: "6614060", price: 1099.99 }),
          m(2, "high", { url: "https://www.walmart.com/ip/5765432120", price: 1099.99 }),
          m(3, "high", { url: "https://www.target.com/p/-/A-91234575", price: 1099.99 }),
          m(4, "not_found", { reason: "Costco does not carry S85F" }),
        ],
      },
      {
        modelNumber: "QN65S85FAFXZA", screenSize: 65, variantLabel: '65"', msrp: 1399.99,
        matches: [
          m(0, "high", { url: "https://www.amazon.com/dp/B0DV4GXK65", sku: "B0DV4GXK65", price: 1397.99 }),
          m(1, "high", { url: "https://www.bestbuy.com/site/6614061.p", sku: "6614061", price: 1399.99 }),
          m(2, "high", { url: "https://www.walmart.com/ip/5765432121", price: 1399.99 }),
          m(3, "high", { url: "https://www.target.com/p/-/A-91234576", price: 1399.99 }),
          m(4, "not_found", { reason: "Costco does not carry S85F" }),
        ],
      },
      {
        modelNumber: "QN77S85FAFXZA", screenSize: 77, variantLabel: '77"', msrp: 1999.99,
        matches: [
          m(0, "high", { url: "https://www.amazon.com/dp/B0DV4GXK77", sku: "B0DV4GXK77", price: 1997.99 }),
          m(1, "high", { url: "https://www.bestbuy.com/site/6614062.p", sku: "6614062", price: 1999.99 }),
          m(2, "medium", { url: "https://www.walmart.com/ip/5765432122", price: 1999.99, reason: "Exact model match but availability shows 'Pre-order'" }),
          m(3, "not_found", { reason: "Target does not carry 77\" S85F" }),
          m(4, "not_found", { reason: "Costco does not carry S85F" }),
        ],
      },
    ],
  },
  // QN90F
  {
    productLineId: "d-pl4",
    productLineName: "Samsung QN90F",
    variants: [
      {
        modelNumber: "QN55QN90FAFXZA", screenSize: 55, variantLabel: '55"', msrp: 1299.99,
        matches: [
          m(0, "high", { url: "https://www.amazon.com/dp/B0DV5GXK55", sku: "B0DV5GXK55", price: 1297.99 }),
          m(1, "high", { url: "https://www.bestbuy.com/site/6614070.p", sku: "6614070", price: 1299.99 }),
          m(2, "high", { url: "https://www.walmart.com/ip/5765432130", price: 1299.99 }),
          m(3, "high", { url: "https://www.target.com/p/-/A-91234585", price: 1299.99 }),
          m(4, "not_found", { reason: "Costco does not carry 55\" QN90F" }),
        ],
      },
      {
        modelNumber: "QN65QN90FAFXZA", screenSize: 65, variantLabel: '65"', msrp: 1699.99,
        matches: [
          m(0, "high", { url: "https://www.amazon.com/dp/B0DV5GXK65", sku: "B0DV5GXK65", price: 1697.99 }),
          m(1, "high", { url: "https://www.bestbuy.com/site/6614071.p", sku: "6614071", price: 1699.99 }),
          m(2, "high", { url: "https://www.walmart.com/ip/5765432131", price: 1699.99 }),
          m(3, "high", { url: "https://www.target.com/p/-/A-91234586", price: 1699.99 }),
          m(4, "high", { url: "https://www.costco.com/samsung-65-qn90f.product.4000234580.html", price: 1599.99 }),
        ],
      },
      {
        modelNumber: "QN75QN90FAFXZA", screenSize: 75, variantLabel: '75"', msrp: 2299.99,
        matches: [
          m(0, "high", { url: "https://www.amazon.com/dp/B0DV5GXK75", sku: "B0DV5GXK75", price: 2297.99 }),
          m(1, "high", { url: "https://www.bestbuy.com/site/6614072.p", sku: "6614072", price: 2299.99 }),
          m(2, "high", { url: "https://www.walmart.com/ip/5765432132", price: 2299.99 }),
          m(3, "not_found", { reason: "Target does not carry 75\" QN90F" }),
          m(4, "high", { url: "https://www.costco.com/samsung-75-qn90f.product.4000234581.html", price: 2149.99 }),
        ],
      },
      {
        modelNumber: "QN85QN90FAFXZA", screenSize: 85, variantLabel: '85"', msrp: 2999.99,
        matches: [
          m(0, "high", { url: "https://www.amazon.com/dp/B0DV5GXK85", sku: "B0DV5GXK85", price: 2997.99 }),
          m(1, "high", { url: "https://www.bestbuy.com/site/6614073.p", sku: "6614073", price: 2999.99 }),
          m(2, "high", { url: "https://www.walmart.com/ip/5765432133", price: 2999.99 }),
          m(3, "not_found", { reason: "Target does not carry 85\" QN90F" }),
          m(4, "medium", { url: "https://www.costco.com/samsung-85-qn90f.product.4000234582.html", price: 2849.99, reason: "Costco-exclusive model QN85QN90FCFXZA — verify variant match" }),
        ],
      },
    ],
  },
];
